import { Navigate, useLocation } from "react-router-dom";
import { useAuth, isCustomer } from "../hooks/useAuth";
import ForcePasswordChange from "../pages/ForcePasswordChange";

/**
 * The three-state gate: asking / signed in / anonymous.
 *
 * Collapsing `loading` into "no user" makes every reload flash the sign-in page before the check
 * returns — on a phone that reads as being logged out at random.
 */
export default function ProtectedRoute({ children, signedOut = null }) {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <span className="spinner-border text-primary" role="status" aria-label="Checking your session" />
      </div>
    );
  }

  // `state.from` returns the customer to the page they wanted after signing in — the usual arrival is
  // a deep link from an SMS or email.
  // Phase 8 (BD-3; MDS §37.4): a route may name what a SIGNED-OUT visitor sees instead (`/` → the public Home).
  // Every other protected route still sends an anonymous visitor to sign-in.
  // Phase 8 follow-up (owner ruling RB-3; MDS §37.17): a session that is NOT a customer's (the admin panel shares the
  // API session) counts as signed out HERE — Home at `/`, Sign In elsewhere. It is never cleared or signed out.
  if (!user || !isCustomer(user)) return signedOut || <Navigate to="/login" state={{ from: location }} replace />;

  // A FORCED PASSWORD CHANGE OUTRANKS THE CUSTOMER CHECK BELOW.
  // `requirePasswordChanged` 403s every customer read while the flag is set, so no portal page can
  // load anything — including `/profile`, whose own voluntary change form is therefore unreachable.
  // Rendered IN PLACE of the requested page rather than redirected to, so there is no route to
  // bypass: every protected path shows this and only this until the flag clears.
  if (mustChangePassword) return <ForcePasswordChange />;

  // (Phase 8 follow-up, RB-3: the former in-place "staff login" notice is gone — a non-customer session is handled
  // above as signed out, and the portal never names staff, admin or a role.)
  return children;
}
